import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useLotsList } from "../api/lotsApi";
import { LotFormDialog } from "../components/LotFormDialog";
import { LotsList } from "../components/LotsList";
import { LotsListHeader } from "../components/LotsListHeader";
import type { Lot } from "../types";

type DialogState = { kind: "closed" } | { kind: "create" } | { kind: "edit"; lot: Lot };

export function LotsListPage() {
  const { currentAccount } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const { data, isLoading, isError, refetch } = useLotsList(accountId);
  const [dialog, setDialog] = useState<DialogState>({ kind: "closed" });

  const lots = data?.lots ?? [];
  const total = lots.length;

  function handleDialogChange(open: boolean) {
    if (!open) setDialog({ kind: "closed" });
  }

  return (
    <>
      <title>Lotes · HATO</title>

      {isLoading ? (
        <>
          <LotsListHeader count={null} onCreate={() => setDialog({ kind: "create" })} />
          <LotsSkeleton />
        </>
      ) : isError ? (
        <>
          <LotsListHeader count={null} onCreate={() => setDialog({ kind: "create" })} />
          <div className="mt-12 flex flex-col items-center gap-4 text-center">
            <p className="text-app-ink-2 text-sm">No se pudieron cargar los lotes.</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
            >
              Reintentar
            </button>
          </div>
        </>
      ) : total === 0 ? (
        <LotsEmptyState onCreate={() => setDialog({ kind: "create" })} />
      ) : (
        <>
          <LotsListHeader count={total} onCreate={() => setDialog({ kind: "create" })} />
          <div className="mt-4">
            <LotsList lots={lots} onEdit={(lot) => setDialog({ kind: "edit", lot })} />
          </div>
        </>
      )}

      <LotFormDialog
        accountId={accountId}
        open={dialog.kind !== "closed"}
        onOpenChange={handleDialogChange}
        mode={dialog.kind === "edit" ? "edit" : "create"}
        lot={dialog.kind === "edit" ? dialog.lot : undefined}
      />
    </>
  );
}

function LotsSkeleton() {
  return (
    <div className="mt-4 space-y-2 md:hidden">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
          key={i}
          className="h-16 animate-pulse rounded-2xl border border-app-border bg-app-surface-2"
        />
      ))}
      <div className="mt-4 hidden h-64 animate-pulse rounded-2xl border border-app-border bg-app-surface-2 md:block" />
    </div>
  );
}

function LotsEmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <h1 className="font-medium text-2xl text-app-ink-1 tracking-tight">Sin lotes todavía</h1>
      <p className="max-w-sm text-app-ink-3 text-sm">
        Los lotes agrupan animales por etapa y son la base para asignar costos. Empezá creando el
        primero.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-2 rounded-2xl bg-app-ink-1 px-4 py-2.5 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d]"
      >
        <Plus size={16} strokeWidth={2} />
        Crear primer lote
      </button>
    </div>
  );
}
